import React, { useState } from 'react';
import { Container, Row, Col, Form } from 'react-bootstrap';
import { ProjectAddress_DB, ProjectModel_DB } from '@models/Models';
import AddressRow from './components/AddressRow';
import { IoIosAddCircle } from "react-icons/io";
import MyButton from '@components/MyButton';
import { useNavigate } from 'react-router-dom';
import useProject from '@hooks/useProject';
import useImage from '@hooks/useImage';
import ImageUploader from '@components/ImageUploader';
import { ProjectStatus } from '@models/Enums';
import useAddress from '@hooks/useAddress';

const AddProject = () => {
    const navi = useNavigate();
    const [addresesList,setAddressesList] = useState<ProjectAddress_DB[]>([
        {
            street: "",
            city: "",
            country: "Deutschland",
            zipCode: "",
            hausnummer: "",
            stiege: "",
        },
    ]);
    const [clientLogo, setClientLogo] = useState<File | null>(null);
    const [clientLogoUrl] = useState<string | null>(null);
    const [projectLogo, setProjectLogo] = useState<File | null>(null);
    const [projectLogoUrl] = useState<string | null>(null);
    const [projectName, setProjectName] = useState("");
    const [maxTime, setMaxTime] = useState("60");
    const [warningAfter, setWarningAfter] = useState("55");
    const { createNewProject } = useProject();
    const { uploadImage } = useImage();
    const { addAddressRow, removeAddressRow } = useAddress();

    const handleClientImageChange = (file: File | null) => {
        setClientLogo(file);
    };
    const handleProjectImageChange = (file: File | null) => {
        setProjectLogo(file);
    };

    const handleAddAddressRow = () => {
        setAddressesList(prevList => [
            ...prevList,
            {
                street: "",
                city: "",
                country: "Deutschland",
                zipCode: "",
                hausnummer: "",
                stiege: "",
            }
        ]);
    };

    const handleRemoveAddressRow = (index: number) => {
        setAddressesList(prevList => prevList.filter((_, i) => i !== index));
    };

    const handleUpdateAddress = (index: number, field: string, value: string) => {
        setAddressesList(prevList => prevList.map((item, i) =>
            i === index ? { ...item, [field]: value } : item
        ));
    };

    const handleSubmit = async () => {
        let clientUrl = clientLogoUrl;
        let projectURL = projectLogoUrl

        if (clientLogo) {
            const logoUploadResult = await uploadImage(clientLogo);
            if (!logoUploadResult.success) {
                alert("Failed to upload logo");
                return;
            }
            clientUrl = logoUploadResult.data as string;
        }
        if (projectLogo) {
            const logoUploadResult = await uploadImage(projectLogo);
            if (!logoUploadResult.success) {
                alert("Failed to upload logo");
                return;
            }
            projectURL = logoUploadResult.data as string;
        }

        const createdAddressIds: string[] = [];

        try {
            for (const address of addresesList) {
                const addressResult = await addAddressRow(address);
                if (!addressResult.success || !addressResult.data?.id) {
                    throw new Error(addressResult.message);
                }
                createdAddressIds.push(addressResult.data.id);
            }

            const newProject: ProjectModel_DB = {
                name: projectName,
                logoClient: clientUrl || "",
                addresses: createdAddressIds,
                projectLogo: projectURL || "",
                infoText: "",
                datenschutzText: "",
                sms1Text: "",
                sms2Text: "",
                employeeTeam: [],
                maxTime: parseInt(maxTime),
                warningAfter: parseInt(warningAfter),
                status: ProjectStatus.ACTIV
            };

            const newProjectResult = await createNewProject(newProject);
            if (!newProjectResult.success) {
                throw new Error(newProjectResult.message);
            }

            navi(`/projekt-texte/${newProjectResult.data?.id}`);
        } catch (error) {
            await Promise.all(createdAddressIds.map(addressId => removeAddressRow(addressId)));
            alert(error instanceof Error ? error.message : "Failed to create project");
        }
    };

    return (
        <Container className='myCont'>
            <Row>
                <Col className='d-flex align-items-center justify-content-center'>
                    <ImageUploader
                        imageUrl={clientLogoUrl}
                        onImageChange={handleClientImageChange}
                    />
                </Col>
                <Col>
                    <Form.Group>
                        <Form.Control
                            placeholder='Projekt Bezeichnung'
                            className='p-1 myBorder d-flex flex-1 w-100'
                            onChange={(e) => setProjectName(e.target.value)}
                            as="textarea"
                            rows={3}
                        />
                    </Form.Group>
                </Col>
                <Col className='d-flex align-items-center justify-content-center'>
                    <ImageUploader
                        imageUrl={projectLogoUrl}
                        onImageChange={handleProjectImageChange}
                    />
                </Col>
            </Row>
            <Row className='my-3'>
                <Col xs={12}>
                    <Row>
                        <Col lg={8}>
                            <Row>
                                <Col>
                                    <h2>Straße</h2>
                                </Col>
                                <Col xs={2} className='p-0'>
                                    <h2>PLZ</h2>
                                </Col>
                                <Col xs={3}>
                                    <h2>Standort</h2>
                                </Col>
                            </Row>
                        </Col>
                        <Col lg={2}>
                            <h2>Haus Nr.</h2>
                        </Col>
                        <Col lg={1}>
                            <h2>Stiege</h2>
                        </Col>
                    </Row>
                    {addresesList.map((address, index) => (
                        <AddressRow
                            key={index}
                            item={address}
                            onRemove={handleRemoveAddressRow}
                            onUpdate={handleUpdateAddress}
                            index={index}
                        />
                    ))}

                    <Row className='my-3'>
                        <Col className='d-flex justify-content-center align-items-center'>
                            <IoIosAddCircle
                                color={'#024442'}
                                size={35}
                                onClick={handleAddAddressRow}
                            />
                        </Col>
                    </Row>
                </Col>
            </Row>
            <Row>
                <Col className='d-flex flex-row justify-content-center align-items-center'>
                    <Form.Group className='d-flex align-items-center gap-2 mb-2'>
                        <Form.Control
                            placeholder='60'
                            value={maxTime}
                            onChange={(e) => setMaxTime(e.target.value)}
                            type="number"
                            className='myBorder w-50'
                        />
                        <Form.Label>Max. <br/> Ladedauer</Form.Label>
                        
                    </Form.Group>

                    <Form.Group className='d-flex align-items-center gap-2'>
                        <Form.Control
                            placeholder='55'
                            value={warningAfter}
                            onChange={(e) => setWarningAfter(e.target.value)}
                            type="number"
                            className='myBorder w-50'
                        />
                        <Form.Label>Warnung <br/> nach</Form.Label>
                        
                    </Form.Group>
                </Col>
                <Col xs={1} className='d-flex justify-content-end gap-3'>
                    <MyButton
                        title='Zurück'
                        disabled={false}
                        isSecundary={true}
                        onClick={() => navi("/")}
                    />
                    <MyButton
                        title='Weiter'
                        disabled={false}
                        isSecundary={false}
                        onClick={handleSubmit}
                    />
                </Col>
            </Row>
        </Container>
    )
}

export default AddProject